import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";

import { fetchRoles, fetchUserPrograms } from "@/shared/apiHelper";
import { useDispatch, flashSuccess, sendFlashMessage } from "@/shared/components/flash";
import AssignRoleModal from "../Assign/AssignRoleModal";
import "../style.scss";

const ProgramRoles = ({ organization, user }) => {
  const dispatch = useDispatch();
  const [keyword, setKeyword] = useState("");
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isAssignRoleOpen, setAssignRoleOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const { data, isLoading, error, refetch } = useQuery(
    ["user-programs", organization?.id, user?.id, refreshTick],
    () => fetchUserPrograms(organization.id, user.id),
    {
      enabled: Boolean(organization?.id && user?.id),
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const { data: roles = [] } = useQuery(
    ["program-role-options", organization?.id],
    () => fetchRoles(organization.id, 1),
    {
      enabled: Boolean(organization?.id),
      staleTime: Infinity,
    }
  );

  const programs = Array.isArray(data) ? data : [];

  const filteredPrograms = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    const uniquePrograms = [];
    const seenPrograms = new Set();

    programs.forEach((program) => {
      const programKey = String(program?.id || program?.name || "");
      if (!programKey || seenPrograms.has(programKey)) {
        return;
      }

      seenPrograms.add(programKey);
      uniquePrograms.push(program);
    });

    if (!normalizedKeyword) {
      return uniquePrograms;
    }

    return uniquePrograms.filter((program) => {
      const name = String(program?.name || "").toLowerCase();
      const id = String(program?.id || "");
      return name.includes(normalizedKeyword) || id.includes(normalizedKeyword);
    });
  }, [keyword, programs]);

  if (isLoading) {
    return (
      <div className="manager-program-user-programs__empty">
        Loading programs...
      </div>
    );
  }

  if (error) {
    return (
      <div className="manager-program-user-programs__empty">
        Unable to load this user&apos;s programs.
      </div>
    );
  }

  if (!programs.length) {
    return (
      <div className="manager-program-user-programs__empty">
        This user does not currently have role access in any programs.
      </div>
    );
  }

  const toggleAssignRoleModal = () => {
    setAssignRoleOpen((prevState) => !prevState);
  };

  const onClickManageRole = (program) => {
    setSelectedProgram({
      ...program,
      organization_id: organization.id,
    });
    setAssignRoleOpen(true);
  };

  const onClickRemoveProgram = (program) => {
    if (!window.confirm("Are you sure to remove this user from this program?")) {
      return;
    }

    axios
      .delete(`/organization/${organization.id}/user/${user.id}/program/${program.id}`)
      .then((response) => {
        if (response.status === 200) {
          flashSuccess(dispatch, "User removed from program successfully!");
          setRefreshTick(Math.floor(Date.now() / 1000));
          refetch();
        }
      })
      .catch((requestError) => {
        dispatch(
          sendFlashMessage(
            requestError?.response?.data?.message || "Unable to remove user from program.",
            "alert-danger",
            "top"
          )
        );
      });
  };

  return (
    <div className="manager-program-user-programs">
      <div className="manager-program-user-programs__toolbar">
        <input
          type="text"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          className="manager-program-user-programs__search"
          placeholder="Search programs by ID or name"
        />
      </div>

      {filteredPrograms.length > 0 ? (
        <div className="manager-program-user-programs__list">
          {filteredPrograms.map((program) => (
            <section
              className="manager-program-user-programs__card"
              key={program.id || program.name}
            >
              <div className="manager-program-user-programs__row">
                {program.id ? (
                  <Link
                    className="manager-program-user-programs__program"
                    to={`/program/view/${program.id}`}
                  >
                    {program.id} - {program.name || `Program ${program.id}`}
                  </Link>
                ) : (
                  <span className="manager-program-user-programs__program">
                    {program.name || "Unknown Program"}
                  </span>
                )}
                {program.id && (
                  <div className="manager-program-user-programs__actions">
                    <button
                      type="button"
                      className="manager-program-user-programs__action-link"
                      onClick={() => onClickRemoveProgram(program)}
                    >
                      Remove
                    </button>
                    <span className="manager-program-user-programs__action-separator">|</span>
                    <button
                      type="button"
                      className="manager-program-user-programs__action-link"
                      onClick={() => onClickManageRole(program)}
                    >
                      Manage Role
                    </button>
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="manager-program-user-programs__empty">
          No programs match your search.
        </div>
      )}

      {selectedProgram && roles.length > 0 && (
        <AssignRoleModal
          key={selectedProgram.id}
          isOpen={isAssignRoleOpen}
          toggle={toggleAssignRoleModal}
          setTrigger={setRefreshTick}
          setParentTrigger={setRefreshTick}
          user={user}
          program={selectedProgram}
          roles={roles}
        />
      )}
    </div>
  );
};

export default ProgramRoles;
